import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import { forgetSessionRenewal, noteSessionRenewed, withSessionRefreshSettled } from "@em/api";

import { User } from "./UserService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const AUTH_USER_FIELDS = `
  id
  tokenExpiresInSec
  username
  email
  emailConfirmation
  uiLanguage
  timeZone
  firstName
  middleName
  lastName
  avatarUrl
  birthday
  status
  userAccounts {
    accountId
    userRoleId
    userRole {
      roleName
    }
  }
  account {
    id
    name
    trialEndsAt
    currentPlan
    appPlanPriceId
    psSubscriptionStatus
    appPlan {
      planName
      planFeaturesDetails {
        featureCode
        featureName
        featureValueType
        featureValue
        status
      }
    }
  }
  userRole {
    id
    roleName
  }
  token
  refreshToken
  fingerprintToken
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const ME_QUERY = gql`
  query Me {
    me {
      code
      data {
        ${AUTH_USER_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const SIGN_IN_MUTATION = gql`
  mutation SignIn($params: SignInInput) {
    signIn(params: $params) {
      code
      data {
        ${AUTH_USER_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

/** What the brand asks of a sign-up (ms_auth, anonymous) — owner, 2026-10-14. */
const SIGN_UP_SETTINGS_QUERY = gql`
  query SignUpSettings {
    signUpSettings {
      code
      data {
        minSignupAge
        requiresCompany
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

/** Close the caller's own account — irreversible (ms_auth AccountCore.close). */
const ACCOUNT_CLOSE_MUTATION = gql`
  mutation AccountClose($params: AccountCloseInput) {
    accountClose(params: $params) {
      code
      data {
        accountId
        closedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

/** A signed-in user's privacy request about their own address (ms_auth PrivacyRequestCore.mine). */
const PRIVACY_REQUEST_MINE_MUTATION = gql`
  mutation PrivacyRequestMine($params: PrivacyRequestInput) {
    privacyRequestMine(params: $params) {
      code
      data {
        id
        status
        dueAt
        answerWithin
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SIGN_UP_MUTATION = gql`
  mutation SignUp($params: SignUpInput) {
    signUp(params: $params) {
      code
      data {
        ${AUTH_USER_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const REFRESH_TOKEN_MUTATION = gql`
  mutation RefreshToken {
    refreshToken {
      code
      data {
        ${AUTH_USER_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SIGN_OUT_MUTATION = gql`
  mutation SignOut {
    signOut {
      code
      data
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RESET_PASSWORD_REQUEST_MUTATION = gql`
  mutation ResetPasswordRequest($params: ResetPasswordRequestInput) {
    resetPasswordRequest(params: $params) {
      code
      data
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword($params: ResetPasswordInput) {
    resetPassword(params: $params) {
      code
      data
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMAIL_CONFIRMATION_REQUEST_MUTATION = gql`
  mutation EmailConfirmationRequest {
    emailConfirmationRequest {
      code
      data
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CHANGE_PASSWORD_MUTATION = gql`
  mutation ChangePassword($params: ChangePasswordInput) {
    changePassword(params: $params) {
      code
      data
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMAIL_CONFIRM_VERIFY_MUTATION = gql`
  mutation EmailConfirmVerify($params: EmailConfirmVerifyInput) {
    emailConfirmVerify(params: $params) {
      code
      data {
        ${AUTH_USER_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface SignInParams {
  username: string;
  password: string;
}

export interface SignUpParams {
  email: string;
  password: string;
  urlQuery?: string;
  httpReferer?: string;
  /** What the account runs — a word from `config/accountTypes` (venue, nonprofit…). */
  accountType?: string | null;
  /** Month and year of birth — required where the brand has a minimum age. */
  birthYear?: number | null;
  birthMonth?: number | null;
  /** The person ticked "I agree to the Terms of Service and the Privacy Policy" — ms_auth records when. */
  acceptTerms?: boolean;
}

/** What a brand asks of a sign-up. */
export interface SignUpSettings {
  /** The youngest age that may register; 0 = no rule. */
  minSignupAge: number;
  /** Whether a company or organization name is required. */
  requiresCompany: boolean;
}

export interface ResetPasswordRequestParams {
  username: string;
}

export interface ResetPasswordParams {
  requestId: string;
  password: string;
  repeatPassword: string;
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * Authentication service that handles all auth-related API calls
 * Uses HTTP-only cookies for authentication (managed by server)
 */
export class AuthService {
  /**
   * Get current user information if authenticated
   * Uses HTTP-only cookies to verify authentication
   */
  static async me(): Promise<OpResult> {
    const result = await ApiService.executeQuery(ME_QUERY);
    return result;
  }

  /**
   * Sign in user with username and password
   */
  static async signIn(params: SignInParams): Promise<OpResult> {
    const result = await ApiService.executeMutation(SIGN_IN_MUTATION, {
      params,
    });

    // Note: Authentication cookies are set by the server automatically
    // No need to store tokens in localStorage since they're HTTP-only cookies
    // #239 — a sign-in issues a session: a renewal, and how long its token lives.
    AuthService.noteRenewal(result);

    return result;
  }

  /**
   * ROADMAP #239 — note a session ISSUED by this answer (sign-in, sign-up, refresh): when, and when its access token expires
   * on THIS device's clock. ⚠ Only from these calls, never from storing a user object: `setUser({ ...user, … })` copies the
   * sign-in answer's lifetime around, and would mark a renewal that never happened.
   */
  private static noteRenewal(result: OpResult): void {
    if (result.code !== OP_RESULT_CODES.OK) {
      return;
    }

    const seconds = Number((result.data?.[0] as User | undefined)?.tokenExpiresInSec);

    noteSessionRenewed(Number.isFinite(seconds) && seconds > 0 ? Date.now() + seconds * 1000 : null);
  }

  /**
   * Sign up new user
   */
  static async signUp(params: SignUpParams): Promise<OpResult> {
    const result = await ApiService.executeMutation(SIGN_UP_MUTATION, {
      params,
    });

    // Note: Authentication cookies are set by the server automatically
    // No need to store tokens in localStorage since they're HTTP-only cookies
    // #242 — a sign-up signs in: it issues a session, like a sign-in.
    AuthService.noteRenewal(result);

    return result;
  }

  /** What this brand asks of a sign-up — anonymous; the form marks its fields with it. */
  static async signUpSettings(): Promise<OpResult> {
    return ApiService.executeQuery(SIGN_UP_SETTINGS_QUERY);
  }

  /**
   * Close the signed-in user's account — IRREVERSIBLE. The account's name typed exactly and the user's password; refusals
   * come back on `confirmName` / `password`.
   */
  static async closeAccount(params: { confirmName: string; password: string }): Promise<OpResult> {
    return ApiService.executeMutation(ACCOUNT_CLOSE_MUTATION, { params });
  }

  /** A privacy request about the signed-in user's own address (kind: access | export | correct | delete | opt_out). */
  static async privacyRequestMine(params: { kind: string; jurisdiction?: string; details?: string }): Promise<OpResult> {
    return ApiService.executeMutation(PRIVACY_REQUEST_MINE_MUTATION, { params });
  }

  /**
   * Refresh authentication token using HTTP-only cookies
   */
  /**
   * ROADMAP #239 — renew the session (the refresh cookie is httpOnly, so no parameters). Registered as the app's session
   * refresher (App.tsx): every call refused for an EXPIRED session waits on it, then repeats.
   * ⚠ It never signs out itself (it used to): a failed refresh is reported once, by the call that asked for it, to the
   *   auth handler — which asks `me()` before it signs anyone out. And it neither retries nor reports on its own refusal
   *   (`sessionRetry` / `reportAuthRefusal` off) — it would wait on itself.
   */
  static async refreshToken(): Promise<OpResult> {
    const result = await ApiService.executeMutation(REFRESH_TOKEN_MUTATION, {}, { sessionRetry: false, reportAuthRefusal: false });
    const user = result.code === OP_RESULT_CODES.OK ? (result.data?.[0] as User | undefined) : undefined;

    if (user) {
      AuthService.storeUserData(user);
    }

    AuthService.noteRenewal(result);

    return result;
  }

  /**
   * Sign out user and clear session data
   * Calls server to clear HTTP-only cookies and clears local data
   */
  static async signOut(): Promise<OpResult> {
    try {
      // Call server signOut mutation to clear HTTP-only cookies.
      // ⚠ #239 — AFTER any refresh in flight and under the same cross-tab lock: a refresh answer landing after it would hand
      //   this browser a NEW session the sign-out never ended. And never renewing itself (`sessionRetry: false`) — it would
      //   wait on the lock it holds.
      const result = await withSessionRefreshSettled(() => ApiService.executeMutation(SIGN_OUT_MUTATION, {}, { sessionRetry: false }));

      // Always clear local session data regardless of server response
      ApiService.clearSession();
      AuthService.clearStoredUserData();

      return result;
    } catch (error) {
      console.error("Error during sign out:", error);

      // Even if server call fails, clear local data
      ApiService.clearSession();
      AuthService.clearStoredUserData();

      // Return error result but still consider it a successful local cleanup
      return new OpResult({
        code: OP_RESULT_CODES.EXCEPTION,
        data: null,
        errors: [
          {
            name: "",
            errors: ["Sign out completed locally, but server error occurred"],
          },
        ],
      });
    }
  }

  /**
   * Request password reset link to be sent to user's email
   */
  static async resetPasswordRequest(
    params: ResetPasswordRequestParams
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      RESET_PASSWORD_REQUEST_MUTATION,
      {
        params,
      }
    );

    return result;
  }

  /**
   * Reset user password using the reset request ID
   */
  static async resetPassword(params: ResetPasswordParams): Promise<OpResult> {
    const result = await ApiService.executeMutation(RESET_PASSWORD_MUTATION, {
      params,
    });

    return result;
  }

  /**
   * Request an email-confirmation code, sent to the signed-in user's account email.
   */
  static async emailConfirmationRequest(): Promise<OpResult> {
    return ApiService.executeMutation(EMAIL_CONFIRMATION_REQUEST_MUTATION, {});
  }

  /**
   * Confirm the signed-in user's email by entering the code they received.
   * Returns the updated user (emailConfirmation = true) on success.
   */
  static async emailConfirmVerify(code: string): Promise<OpResult> {
    return ApiService.executeMutation(EMAIL_CONFIRM_VERIFY_MUTATION, { params: { code } });
  }

  /**
   * Change the signed-in user's password. Verifies the current password server-side;
   * field errors (currentPassword / newPassword / repeatPassword) come back keyed.
   */
  static async changePassword(params: {
    currentPassword: string;
    newPassword: string;
    repeatPassword: string;
  }): Promise<OpResult> {
    return ApiService.executeMutation(CHANGE_PASSWORD_MUTATION, { params });
  }

  /**
   * Check if user appears to be authenticated
   * Note: This is a best guess since we can't read HTTP-only cookies
   * The server will validate actual authentication
   */
  static isAuthenticated(): boolean {
    // Since we can't read HTTP-only cookies, we check if user data exists
    const userData = localStorage.getItem("eventsmagic_user");
    return Boolean(userData);
  }

  /**
   * Get stored user data (not tokens, as they're in HTTP-only cookies)
   */
  static getStoredUserData(): User | null {
    try {
      const userData = localStorage.getItem("eventsmagic_user");
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error("Error parsing stored user data:", error);
      return null;
    }
  }

  /**
   * Store user data in localStorage (not tokens)
   */
  static storeUserData(userData: User): void {
    // Remove sensitive data before storing (#239: and the token's lifetime — a fact about one answer, not about the user)
    const { token, refreshToken, fingerprintToken, tokenExpiresInSec, ...safeUserData } = userData;

    localStorage.setItem("eventsmagic_user", JSON.stringify(safeUserData));
  }

  /**
   * Clear stored user data
   */
  static clearStoredUserData(): void {
    localStorage.removeItem("eventsmagic_user");
    // #239 — signed out: no renewal and no token time to refresh ahead of.
    forgetSessionRenewal();
  }
}